import { useEffect, useRef, useState } from 'react';
import {
  animate as animateMotion,
  motion,
  useMotionValue,
  useTransform,
} from 'motion/react';
import journeyPath from '@/assets/imgs/home/journey/path.png';
import paperPlane from '@/assets/imgs/home/journey/plane.svg';
import { useDesignScale } from '@/hooks/useDesignScale';

const journeyPathOffset = [110, 647] as const;
const journeyPathPoints = [
  [1317, 0],
  [1442, 56],
  [1540, 128],
  [1619, 221],
  [1673.5, 330],
  [1698.5, 449],
  [1694.5, 580],
  [1671.5, 677],
  [1627, 767],
  [1515.5, 881],
  [1357, 967],
  [650.5, 1165],
  [302, 1365],
  [148, 1500],
  [84.5, 1593],
  [42.5, 1699],
  [33.5, 1828],
  [65, 1944],
  [142, 2061],
  [261, 2168],
  [397.5, 2252],
  [637, 2350],
  [1338, 2489],
  [1597.5, 2641],
  [1662, 2738],
  [1690.5, 2839],
  [1696.5, 2985],
  [1671, 3103],
  [1633.5, 3173],
  [1563.5, 3245],
  [1477, 3297],
  [1383.5, 3331],
  [1166, 3372],
  [513, 3426],
  [354.5, 3459],
  [243, 3500],
  [157, 3551],
  [73, 3637],
  [21, 3745],
  [1.5, 3865],
  [7, 3954],
  [42.5, 4046],
  [105, 4124],
  [185, 4183],
  [403.5, 4268],
  [831, 4335],
  [1061, 4401],
  [1166, 4459],
  [1226, 4513],
  [1269, 4580],
  [1287.5, 4662],
] as const;

const initialPlanePosition = {
  offsetDistance: '0%',
};
const initialPlaneProgress = 0;
const planeTravelSpeed = 9;

const createJourneyPathData = (scale: number) => {
  const points = journeyPathPoints.map(([x, y]) => [
    (x + journeyPathOffset[0]) * scale,
    (y + journeyPathOffset[1]) * scale,
  ]);
  const [startX, startY] = points[0];
  const curves = points.slice(0, -1).map((point, index) => {
    const previous = points[Math.max(0, index - 1)];
    const next = points[index + 1];
    const afterNext = points[Math.min(points.length - 1, index + 2)];
    const controlOne = [
      point[0] + (next[0] - previous[0]) / 6,
      point[1] + (next[1] - previous[1]) / 6,
    ];
    const controlTwo = [
      next[0] - (afterNext[0] - point[0]) / 6,
      next[1] - (afterNext[1] - point[1]) / 6,
    ];

    return `${controlOne.join(' ')} ${controlTwo.join(' ')} ${next.join(' ')}`;
  });

  return `M ${startX} ${startY} C ${curves.join(' C ')}`;
};

const createJourneyOffsetPath = (scale: number) =>
  `path("${createJourneyPathData(scale)}")`;

type JourneyPosition = {
  offsetPercent: number;
  visible: boolean;
};

const JourneyPlane = () => {
  const designScale = useDesignScale();
  const journeyRef = useRef<HTMLDivElement>(null);
  const planeProgress = useMotionValue(initialPlaneProgress);
  const planeOffsetDistance = useTransform(planeProgress, value => `${value}%`);
  const [journeyPosition, setJourneyPosition] = useState<JourneyPosition>({
    offsetPercent: initialPlaneProgress,
    visible: false,
  });

  useEffect(() => {
    const journeyContainer = journeyRef.current?.parentElement;
    if (!journeyContainer) return;

    const measurementPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    measurementPath.setAttribute('d', createJourneyPathData(1));
    const totalPathLength = measurementPath.getTotalLength();
    const pathStartY = journeyPathOffset[1] + journeyPathPoints[0][1];
    const pathEndY = journeyPathOffset[1] + journeyPathPoints[journeyPathPoints.length - 1][1];

    let animationFrame = 0;

    const findOffsetDistance = (targetY: number) => {
      let lowerLength = 0;
      let upperLength = totalPathLength;

      for (let iteration = 0; iteration < 18; iteration += 1) {
        const middleLength = (lowerLength + upperLength) / 2;
        const point = measurementPath.getPointAtLength(middleLength);

        if (point.y < targetY) {
          lowerLength = middleLength;
        } else {
          upperLength = middleLength;
        }
      }

      return ((lowerLength + upperLength) / 2 / totalPathLength) * 100;
    };

    const updateJourneyPosition = () => {
      animationFrame = 0;
      const containerRect = journeyContainer.getBoundingClientRect();
      const viewportCenterInContainer = (window.innerHeight / 2 - containerRect.top) / designScale;
      const isWithinJourney = viewportCenterInContainer >= pathStartY
        && viewportCenterInContainer <= pathEndY;
      const offsetPercent = isWithinJourney
        ? findOffsetDistance(viewportCenterInContainer)
        : viewportCenterInContainer > pathEndY ? 100 : 0;
      const nextPosition = {
        offsetPercent,
        visible: isWithinJourney,
      };

      setJourneyPosition(current =>
        Math.abs(current.offsetPercent - nextPosition.offsetPercent) < 0.001
          && current.visible === nextPosition.visible
          ? current
          : nextPosition,
      );
    };

    const scheduleUpdate = () => {
      if (animationFrame) return;
      animationFrame = window.requestAnimationFrame(updateJourneyPosition);
    };

    updateJourneyPosition();
    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate);

    return () => {
      window.removeEventListener('scroll', scheduleUpdate);
      window.removeEventListener('resize', scheduleUpdate);
      if (animationFrame) window.cancelAnimationFrame(animationFrame);
    };
  }, [designScale]);

  useEffect(() => {
    const currentProgress = planeProgress.get();
    const distance = Math.abs(journeyPosition.offsetPercent - currentProgress);

    if (distance < 0.001) {
      planeProgress.set(journeyPosition.offsetPercent);
      return;
    }

    const controls = animateMotion(planeProgress, journeyPosition.offsetPercent, {
      duration: distance / planeTravelSpeed,
      ease: 'linear',
    });

    return () => controls.stop();
  }, [journeyPosition.offsetPercent, planeProgress]);

  return (
    <div
      ref={journeyRef}
      className="overflow-hidden absolute inset-0 pointer-events-none z-[8]"
      aria-hidden
    >
      <div className="absolute left-1/2 top-0 h-full w-[1920px] -translate-x-1/2">
        <img
          src={journeyPath}
          alt=""
          data-node-id="10524:23075"
          className="absolute left-[110px] top-[647px] z-10 h-[4663px] w-[1703px]"
        />

        <motion.div
          key="journey-plane"
          data-node-id="10524:23121"
          data-motion-wrapper-for="10524:23121"
          data-journey-offset={journeyPosition.offsetPercent}
          className="absolute left-0 top-0 z-30 flex h-[46px] w-[53px] items-center justify-center will-change-transform"
          style={{
            offsetPath: createJourneyOffsetPath(designScale),
            // Runtime rotation comes only from the path tangent. The child
            // below performs the one-time artwork baseline calibration.
            offsetRotate: 'auto',
            offsetDistance: planeOffsetDistance,
          }}
          initial={{ opacity: 0, ...initialPlanePosition }}
          animate={{
            opacity: journeyPosition.visible ? 1 : 0,
          }}
          transition={{
            opacity: {
              duration: 0.2,
              ease: 'linear',
            },
          }}
        >
          <div className="flex-none rotate-[26.519deg]">
            <img src={paperPlane} alt="" className="h-[29px] w-[45px]" />
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default JourneyPlane;
